import { FC, useState, useMemo } from 'react'
import {
  useRequestMandateForMembership,
  useSendMembershipSms,
  useCustomerMembership,
} from 'api/Memberships'
import { useSmsSettings } from 'api/SmsNotifications'
import { ResponseError, SendMembershipSmsMessageTypeEnum } from '@trybeapp/sdk'
import { Notification, useNotificationContext } from 'ui/components/Notification'
import { Text } from 'ui/components/Text'
import { ConfirmationModal, Modal, ModalButtons, ModalProps } from 'ui/Modal'
import { Button } from 'ui/components/Button'
import { RadioCards, RadioCardOption } from 'ui/components/Form/RadioCards/RadioCards'
import { Label } from 'ui/components/Form/Label'
import { MailIcon, ChatAlt2Icon } from '@heroicons/react/solid'
import { useActiveFeatureFlags } from 'contexts/SiteConfig'
import { useTranslation } from 'react-i18next'

type SendMethod = 'email' | 'sms'

interface SendMethodOption extends Omit<RadioCardOption, 'id'> {
  id: SendMethod
}

interface RequestMandateModalProps extends Pick<ModalProps, 'isOpen' | 'onClose'> {
  membershipId: string | null
}

export const RequestMandateModal: FC<RequestMandateModalProps> = ({
  isOpen,
  onClose,
  membershipId,
}) => {
  const { t } = useTranslation()
  const notification = useNotificationContext()
  const featureFlags = useActiveFeatureFlags()
  const [preferredMethod, setPreferredMethod] = useState<SendMethod>('email')

  const { data: { data: membership } = {} } = useCustomerMembership(membershipId ?? '', {
    enabled: !!membershipId && isOpen,
  })
  const { data: { data: smsSettings } = {} } = useSmsSettings()
  const { isLoading: isLoadingEmail, mutate: sendEmail } = useRequestMandateForMembership()
  const { isLoading: isLoadingSms, mutate: sendSms } = useSendMembershipSms()

  const isLoading = isLoadingEmail || isLoadingSms

  const leadMember = membership?.customer
  const hasEmail = !!leadMember?.email
  const hasPhone = !!leadMember?.phone

  const smsFeatureEnabled = featureFlags.includes('sms_notifications')
  const billingDetailsSmsEnabled = smsSettings?.billing_details_request_sms_enabled

  const canSendSms = hasPhone && smsFeatureEnabled && billingDetailsSmsEnabled

  const options = useMemo<SendMethodOption[]>(() => {
    const result: SendMethodOption[] = []
    if (hasEmail) {
      result.push({
        id: 'email',
        title: t('clients.memberships.request_mandate_modal.option_email'),
        icon: MailIcon,
      })
    }
    if (canSendSms) {
      result.push({
        id: 'sms',
        title: t('clients.memberships.request_mandate_modal.option_sms'),
        icon: ChatAlt2Icon,
      })
    }
    return result
  }, [hasEmail, canSendSms, t])

  const sendMethod = useMemo<SendMethod>(() => {
    if (preferredMethod === 'email' && hasEmail) return 'email'
    if (preferredMethod === 'sms' && canSendSms) return 'sms'
    if (hasEmail) return 'email'
    if (canSendSms) return 'sms'
    return 'email'
  }, [preferredMethod, hasEmail, canSendSms])

  const selectedOption = options.find((option) => option.id === sendMethod)

  const handleSend = () => {
    if (!membershipId) return

    if (sendMethod === 'email') {
      sendEmail(membershipId, {
        onSuccess: () => {
          onClose(false)
          notification.notify(
            <Notification
              title={t('clients.memberships.request_mandate_modal.success_notification.title')}
              description={t(
                'clients.memberships.request_mandate_modal.success_notification.email_description'
              )}
              variant="success"
              autoDismiss
            />
          )
        },
        onError: (e: ResponseError) => {
          onClose(false)
          notification.notifyApiError(e)
        },
      })
      return
    }

    sendSms(
      {
        membershipId,
        sendMembershipSms: {
          message_type: SendMembershipSmsMessageTypeEnum.BillingDetailsRequest,
          always_send: true,
        },
      },
      {
        onSuccess: () => {
          onClose(false)
          notification.notify(
            <Notification
              title={t('clients.memberships.request_mandate_modal.success_notification.title')}
              description={t(
                'clients.memberships.request_mandate_modal.success_notification.sms_description'
              )}
              variant="success"
              autoDismiss
            />
          )
        },
        onError: (e) => {
          onClose(false)
          notification.notifyApiError(e)
        },
      }
    )
  }

  const showSimpleModal = options.length <= 1

  if (showSimpleModal) {
    const canSend = options.length > 0
    const isSmsOnly = options[0]?.id === 'sms'

    const getDescription = () => {
      if (!canSend) {
        if (!hasEmail && !hasPhone) {
          return t('clients.memberships.request_mandate_modal.no_contact_info')
        }
        return t('clients.memberships.request_mandate_modal.no_email')
      }
      return isSmsOnly
        ? t('clients.memberships.request_mandate_modal.description_sms')
        : t('clients.memberships.request_mandate_modal.description_email')
    }

    return (
      <ConfirmationModal
        isOpen={isOpen}
        title={t('clients.memberships.request_mandate_modal.title')}
        onClose={onClose}
        onConfirm={handleSend}
        confirming={isLoading}
        confirmButtonLabel={
          isSmsOnly
            ? t('clients.memberships.request_mandate_modal.send_sms_button')
            : t('clients.memberships.request_mandate_modal.send_email_button')
        }
        confirmButtonIcon={isSmsOnly ? ChatAlt2Icon : MailIcon}
        variant="info"
        disabled={!canSend}
      >
        <Text type="caption">{getDescription()}</Text>
      </ConfirmationModal>
    )
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('clients.memberships.request_mandate_modal.title')}
      size="md"
      showHideIcon={false}
    >
      <div className="space-y-4">
        <Text type="caption">
          {t('clients.memberships.request_mandate_modal.description_with_name', {
            name: leadMember?.first_name,
          })}
        </Text>

        <div className="space-y-2">
          <Label text={t('clients.memberships.request_mandate_modal.send_by_label')} />
          <RadioCards
            options={options}
            value={selectedOption}
            onChange={(option: SendMethodOption) => setPreferredMethod(option.id)}
          />
        </div>
      </div>

      <ModalButtons>
        <Button
          label={
            sendMethod === 'sms'
              ? t('clients.memberships.request_mandate_modal.send_sms_button')
              : t('clients.memberships.request_mandate_modal.send_email_button')
          }
          variant="primary"
          onClick={handleSend}
          loading={isLoading}
          leadingIcon={sendMethod === 'sms' ? ChatAlt2Icon : MailIcon}
        />
        <Button label={t('modal_button.cancel')} onClick={() => onClose(false)} />
      </ModalButtons>
    </Modal>
  )
}
